import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, tap, toArray } from 'rxjs/operators';
import { formatSignedAt } from '../../utils/date.utils';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { SignService } from '../../services/sign.service';
import { UpqueueService } from '../../services/upqueue.service';
import { DeviceCryptoService } from '../../services/device-crypto.service';
import { ToastService } from '../../../shared/services/toast.service';
import { acceptJobsToPayload } from './index';
import { decodeEncryptedDocument } from './adapter.utils';
import type { Submission } from '../../models/submission.model';

// ─── Internal types ─────────────────────────────────────────────────────────

/**
 * 4xx statuses that must NOT be treated as permanent, despite being 4xx.
 *
 * @remarks
 * A `permanent` classification marks the queue item `rejected` (see
 * {@link UpqueueAdapter.handleResults}) — since 2026-10-09 it is never
 * deleted, but it stops being retried automatically and needs a manual
 * "Transmit all". So the bar for "permanent" is still not "the server said
 * 4xx", it is "retrying this exact payload can never succeed".
 * These three fail that test:
 *
 * - **408 Request Timeout** and **429 Too Many Requests** are explicitly
 *   retryable by definition — the payload was never rejected on its merits.
 * - **413 Payload Too Large** is the important one (2026-08-05). Evidence
 *   photos are stored and transmitted at full capture resolution — no
 *   client-side downscale (see the "NO CLIENT-SIDE PHOTO COMPRESSION" note in
 *   `photo.service.ts`) — so an over-size body is a realistic outcome against
 *   an unknown server limit, and a server-side limit change can make it
 *   succeed later — so it keeps auto-retrying rather than waiting for a
 *   manual "Transmit all".
 *
 * **Known trade-off:** a genuinely un-shrinkable payload retries on every
 * sync cycle. That is deliberate — a repeating failure the officer can see
 * beats silent evidence loss.
 */
const RETRYABLE_4XX = new Set([408, 413, 429]);

/**
 * Whether a failure status means "retrying this payload can never succeed".
 *
 * @param status - HTTP status, or `0` for a network-level failure.
 * @returns `true` only for a genuinely permanent rejection.
 */
function isPermanentStatus(status: number): boolean {
  if (status < 400 || status >= 500) return false; // 5xx + status-0 network: transient.
  if (status === 401) return false;                // Auth interceptor owns refresh/retry.
  return !RETRYABLE_4XX.has(status);
}

/**
 * Outcome of a single transmission — used to partition results after `toArray()`.
 *
 * @remarks
 * `permanent` is `true` when the server returned a 4xx error, excluding 401 and
 * the {@link RETRYABLE_4XX} statuses. These items are NOT deleted (2026-10-09):
 * they are marked `rejected` in the queue — skipped by automatic uploads, still
 * blocking logout, re-sent only by a manual "Transmit all". 5xx, status-0
 * (network), 401 and the retryable 4xx set are transient and kept for the next
 * sync cycle.
 */
interface TransmitResult {
  item:      Submission;
  success:   boolean;
  /** `true` for a permanently invalid payload — see {@link isPermanentStatus}. */
  permanent: boolean;
  /** HTTP status of the failure (`0` for network / non-HTTP errors). */
  status:    number;
}

/** Extracts a `{ latitude, longitude }` object from a stored payload's `location` field. */
function extractCoords(
  payload: Record<string, unknown>,
): { latitude: number; longitude: number } | null {
  const loc = payload['location'] as { latitude?: unknown; longitude?: unknown } | undefined;
  if (loc == null) return null;
  const lat = Number(loc['latitude']);
  const lng = Number(loc['longitude']);
  return !isNaN(lat) && !isNaN(lng) ? { latitude: lat, longitude: lng } : null;
}

/** Extracts `affidavit_sign_status` (`'A'` | `'R'`) from a stored affidavit payload's first item. */
function extractSignStatus(payload: Record<string, unknown>): string | undefined {
  const items = payload['signed_affidavits'] as { affidavit_sign_status?: string }[] | undefined;
  return items?.[0]?.affidavit_sign_status;
}

/** Options for {@link UpqueueAdapter.transmitAll}. */
export interface TransmitOptions {
  /**
   * When `true`, suppresses the "Nothing pending to upqueue." info toast.
   * Use during automated sync cycles to avoid noisy toasts on empty queues.
   */
  quiet?: boolean;
  /**
   * When `true`, suppresses ALL result toasts (empty, success, failure, permanent).
   * Use during automated background sync cycles (`fullSync`, `autoSync`) so the
   * process server does not see error alerts on every dashboard refresh.
   * The upqueue badge still reflects the pending count visually.
   */
  background?: boolean;
  /**
   * `true` only for an explicit officer action ("Transmit all" on `/upqueue`,
   * or the header refresh while on `/upqueue`). Includes items the server
   * previously rejected (`Submission.rejected`); every automatic upload
   * (sync, reconnect, post-submit) leaves this unset and skips them.
   */
  manual?: boolean;
}

// ─── Adapter ─────────────────────────────────────────────────────────────────

/**
 * Transmits all pending submissions from {@link UpqueueService} to the correct
 * Tinite API endpoints, processing them sequentially to preserve submission order.
 *
 * @remarks
 * **Routing:**
 * - `"attempt"` items → `POST /process-server/{serverId}/jobs/upqueue`
 * - `"affidavit"` items → `POST /process-server/{serverId}/jobs/esignqueue`
 *
 * **Partial-failure guarantee:** Successful items are removed via
 * {@link UpqueueService.applyResults}. Permanently-failed (4xx) items are
 * NEVER removed — they are marked `rejected` and only re-sent by a manual
 * "Transmit all" (`{ manual: true }`). Transient failures (5xx, status-0)
 * remain in the store and are eligible for the next `transmitAll()`.
 *
 * **Toast messages (matches UX spec):**
 * - All succeeded         → success green  `"Submission received."`
 * - Transient failures    → danger red     `"N submitted, M failed — tap Upqueue to retry."`
 * - Permanent 4xx kept    → warning yellow `"N submission(s) rejected by server..."`
 * - Empty queue           → dark brand     `"Nothing pending to upqueue."`
 * - `background: true`    → all toasts suppressed; upqueue badge shows count visually.
 *
 * @example
 * ```ts
 * inject(UpqueueAdapter).transmitAll().subscribe();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class UpqueueAdapter {
  private readonly http           = inject(HttpClient);
  private readonly authService    = inject(AuthService);
  private readonly upqueueService = inject(UpqueueService);
  private readonly signService    = inject(SignService);
  private readonly toastService   = inject(ToastService);
  private readonly deviceCrypto   = inject(DeviceCryptoService);

  /**
   * `true` while a {@link transmitAll} call is actively processing queue items.
   *
   * @remarks
   * Exposed as a public Signal so all callers — the Upqueue page button,
   * `NetworkService` auto-retry, and `SyncService` flush — share a single
   * source of truth for in-flight state. Prevents concurrent transmissions from
   * posting the same item twice.
   */
  readonly transmitting = signal(false);

  /**
   * Transmits all pending items from {@link UpqueueService} to the API.
   *
   * @remarks
   * Items are processed sequentially via `concatMap` so that the second API
   * call does not begin until the first completes. This preserves chronological
   * order and avoids overwhelming the server with concurrent requests.
   *
   * The Observable always completes — errors on individual items are caught per-item
   * and recorded as failures rather than terminating the stream.
   *
   * Automatic callers send only items without a `rejected` marker; a queue
   * holding nothing but rejected items is a silent no-op for them.
   *
   * @param options - Optional transmission options.
   * @param options.quiet      - Suppress the empty-queue info toast.
   * @param options.background - Suppress ALL result toasts (for background sync cycles).
   * @param options.manual     - Officer-initiated: also re-send rejected items.
   * @returns Observable that emits `void` once all items have been processed.
   *   Returns immediately with `of(void 0)` (no HTTP call, no toast) when a
   *   transmission is already in progress — preventing duplicate POSTs.
   */
  transmitAll(options?: TransmitOptions): Observable<void> {
    if (this.transmitting()) return of(void 0 as void);

    const all   = this.upqueueService.items();
    const queue = options?.manual ? all : all.filter(i => !i.rejected);

    if (queue.length === 0) {
      if (all.length === 0 && !options?.quiet && !options?.background) {
        void this.toastService.info('Nothing pending to upqueue.');
      }
      return of(void 0 as void);
    }

    const token = this.authService.token();
    if (!token) return of(void 0 as void);

    const serverId = token.user.entityId;

    this.transmitting.set(true);
    return from(queue).pipe(
      concatMap((item) =>
        this.transmitOne(item, serverId).pipe(
          map(() => ({ item, success: true, permanent: false, status: 200 }) as TransmitResult),
          catchError((err: unknown) => {
            const status = err instanceof HttpErrorResponse ? err.status : 0;
            return of({ item, success: false, permanent: isPermanentStatus(status), status } as TransmitResult);
          }),
        ),
      ),
      toArray(),
      tap((results) => this.handleResults(results, serverId, options)),
      map(() => void 0 as void),
      finalize(() => this.transmitting.set(false)),
    );
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  /**
   * Routes a single queued item to the correct API endpoint.
   *
   * @param item     - The submission to transmit.
   * @param serverId - Authenticated process server entity ID.
   */
  private transmitOne(item: Submission, serverId: number): Observable<void> {
    if (item.type === 'attempt') {
      return this.transmitAttempt(item, serverId);
    }
    if (item.type === 'accept') {
      return this.transmitAccept(item, serverId);
    }
    return this.transmitAffidavit(item, serverId);
  }

  /**
   * Transmits an attempt submission to `POST /jobs/upqueue`.
   * The stored payload is a single `UpQueueJobItem`; it is wrapped in
   * `{ jobs: [...] }` as the batch endpoint requires.
   *
   * @param item     - Queued attempt submission.
   * @param serverId - Process server entity ID.
   */
  private transmitAttempt(item: Submission, serverId: number): Observable<void> {
    return this.http
      .post<unknown>(
        `${environment.apiUrl}/process-server/${serverId}/jobs/upqueue`,
        { jobs: [item.payload] },
      )
      .pipe(map(() => void 0 as void));
  }

  /**
   * Transmits a queued accept submission to `POST /jobs/acceptqueue`.
   * The stored payload contains `job_numbers: number[]`.
   *
   * @param item     - Queued accept submission.
   * @param serverId - Process server entity ID.
   */
  private transmitAccept(item: Submission, serverId: number): Observable<void> {
    const jobNums = (item.payload['job_numbers'] as number[]) ?? [];
    return this.http
      .post<unknown>(
        `${environment.apiUrl}/process-server/${serverId}/jobs/acceptqueue`,
        acceptJobsToPayload(jobNums),
      )
      .pipe(map(() => void 0 as void));
  }

  /**
   * Transmits an affidavit submission and updates {@link SignService} on
   * confirmed success — `markSigned()` for an approval, `markRejectedByPS()`
   * for a rejection (determined by the queued payload's
   * `affidavit_sign_status`).
   *
   * @remarks
   * **Decrypts the device-encrypted document before POSTing (2026-08-05).**
   * `AffidavitAdapter.buildQueuedPayload()` wraps `affidavit_sign_document`
   * with a device-bound key before enqueueing, purely to protect it at rest
   * in `IDB_STORE_QUEUE` — the server has no knowledge of that extra layer and
   * expects exactly the original CRYPTOJSKEY-ciphertext string
   * `AffidavitComponent.submitAffidavit()` produced. So this method must undo
   * the device-level wrapping (via {@link decodeEncryptedDocument} +
   * `DeviceCryptoService.decrypt()`) before the HTTP call, using a shallow-
   * cloned payload — `item.payload` itself, and therefore what
   * `extractSignStatus()`/`extractCoords()` read below, is left untouched.
   *
   * @param item     - Queued affidavit submission.
   * @param serverId - Process server entity ID.
   */
  private transmitAffidavit(item: Submission, serverId: number): Observable<void> {
    return from(this.decryptAffidavitDocument(item.payload)).pipe(
      switchMap(payload =>
        this.http.post<unknown>(
          `${environment.apiUrl}/process-server/${serverId}/jobs/esignqueue`,
          payload,
        ),
      ),
      tap(() => {
        if (extractSignStatus(item.payload) === 'R') {
          this.signService.markRejectedByPS(item.jobId);
          return;
        }
        const coords = extractCoords(item.payload);
        this.signService.markSigned(item.jobId, formatSignedAt(new Date(item.timestamp)), coords);
      }),
      map(() => void 0 as void),
    );
  }

  /**
   * Undoes {@link AffidavitAdapter}'s device-level encryption of
   * `affidavit_sign_document`, if present, returning a payload safe to POST.
   *
   * @remarks
   * `decodeEncryptedDocument()` returns `null` when the field was never
   * device-encrypted in the first place — a rejection record (no document at
   * all), or the rare case where `buildQueuedPayload()`'s own encryption step
   * failed and left the plain CRYPTOJSKEY-ciphertext value in place. Both are
   * treated identically here: the payload is sent through unchanged.
   *
   * **A genuine decrypt failure is left to reject and propagate.** This can
   * only really happen if `IDB_STORE_CRYPTO_KEYS` were cleared between
   * enqueue and transmit — which does not happen mid-session (see
   * `DeviceCryptoService`'s doc comment: the key is generated once and cached
   * for the service's lifetime) and not across a restart or logout either —
   * nothing in the app clears that store (`AuthService.clearAllStorage()`
   * deliberately keeps it alongside `IDB_STORE_QUEUE` so queued affidavits
   * survive logout). Only an external site-data wipe could orphan one. If this ever
   * rejects anyway, `transmitAll()`'s `catchError` classifies a non-
   * `HttpErrorResponse` as status `0` — the same "transient, kept for the next
   * sync cycle" bucket as a network failure — never a permanent deletion; the
   * alternative (silently sending raw ciphertext, or dropping the submission)
   * would either corrupt the server record or destroy the officer's only copy
   * of a signed legal document.
   *
   * @param payload - The queued item's stored payload.
   * @returns The same payload, with `affidavit_sign_document` restored to its
   *          original (server-expected) value when it had been device-encrypted.
   */
  private async decryptAffidavitDocument(
    payload: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const affidavits = payload['signed_affidavits'] as Array<Record<string, unknown>> | undefined;
    const item = affidavits?.[0];
    const encoded = item?.['affidavit_sign_document'];
    if (!item || typeof encoded !== 'string') return payload;

    const encrypted = decodeEncryptedDocument(encoded);
    if (!encrypted) return payload; // Not device-encrypted — send as-is.

    const plaintext = await this.deviceCrypto.decrypt(encrypted); // may reject — see this method's doc comment.
    return {
      ...payload,
      signed_affidavits: [{ ...item, affidavit_sign_document: plaintext }],
    };
  }

  /**
   * Removes successful items, marks permanently-rejected ones, and shows the
   * appropriate result toast.
   *
   * @remarks
   * **Nothing is ever deleted without reaching the server (2026-10-09).**
   * Permanent failures stay in the queue with a `rejected` marker — they hold
   * the only copy of the item's photos, notes and GPS. They keep blocking
   * logout, are skipped by automatic uploads, and are re-sent only by a manual
   * "Transmit all". Transient failures (5xx, network, 401, and the
   * {@link RETRYABLE_4XX} statuses 408/413/429) are kept for the next sync
   * cycle unchanged. When `options.background` is `true`, all toasts are
   * suppressed so background auto-sync does not interrupt the process server.
   *
   * `serverId` is passed through to `UpqueueService.applyResults()` so that,
   * if the officer logged out while this transmission was in flight, the
   * outcome is applied to THEIR stored queue (which survives logout).
   *
   * @param results  - Array of per-item transmission outcomes from `toArray()`.
   * @param serverId - Entity ID the items were transmitted for.
   * @param options  - Transmission options forwarded from {@link transmitAll}.
   */
  private handleResults(results: TransmitResult[], serverId: number, options?: TransmitOptions): void {
    const succeeded = results.filter((r) => r.success).map((r) => r.item);
    const permanent = results.filter((r) => !r.success && r.permanent);
    const transient = results.filter((r) => !r.success && !r.permanent);

    this.upqueueService.applyResults(
      succeeded,
      permanent.map(r => ({ item: r.item, status: r.status })),
      serverId,
    );

    if (options?.background) return;

    if (permanent.length > 0) {
      void this.toastService.warning(
        `${permanent.length} submission(s) rejected by server — kept in Upqueue. Tap Transmit all to retry.`,
      );
    }
    if (transient.length === 0 && succeeded.length > 0 && permanent.length === 0) {
      void this.toastService.success('Submission received.');
    } else if (transient.length > 0) {
      void this.toastService.error(
        `${succeeded.length} submitted, ${transient.length} failed — tap Upqueue to retry.`,
      );
    }
  }
}
